export type BulkCreationDetailRow = {
  apartmentCode: string;
  feeCategory: string;
  action: string;
  message?: string;
};

export type BulkCreationCategorySummary = {
  feeCategory: string;
  feeCode?: string | null;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
};

export type NormalizedBulkCreationDetails = {
  rows: BulkCreationDetailRow[];
  warnings: string[];
  categorySummaries: BulkCreationCategorySummary[];
};

export type BulkCreationActionFilter =
  | 'all'
  | 'created'
  | 'updated'
  | 'skipped'
  | 'error';

type BulkSkipLog = {
  apartmentCode: string;
  action: 'skipped';
  message: string;
};

type ParkingBulkAuditStored = {
  feeCategoryName: string;
  skipLogs?: BulkSkipLog[];
};

type CategorySkipAuditStored = {
  feeCategoryName: string;
  skipLogs?: BulkSkipLog[];
};

function readParkingAudits(details: unknown): ParkingBulkAuditStored[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return [];
  }
  const audits = (details as { parkingAudits?: ParkingBulkAuditStored[] }).parkingAudits;
  return Array.isArray(audits) ? audits : [];
}

function readCategorySkipAudits(details: unknown): CategorySkipAuditStored[] {
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return [];
  }
  const audits = (details as { categorySkipAudits?: CategorySkipAuditStored[] })
    .categorySkipAudits;
  return Array.isArray(audits) ? audits : [];
}

function expandSkipLogsFromAudits(
  audits: Array<{ feeCategoryName: string; skipLogs?: BulkSkipLog[] }>,
): BulkCreationDetailRow[] {
  const expanded: BulkCreationDetailRow[] = [];
  for (const audit of audits) {
    for (const log of audit.skipLogs ?? []) {
      expanded.push({
        apartmentCode: log.apartmentCode,
        feeCategory: audit.feeCategoryName,
        action: 'skipped',
        message: log.message,
      });
    }
  }
  return expanded;
}

/** Full row list for lịch sử modal — merges skipLogs (parking + fixed PQL) not capped in rows. */
export function buildBulkCreationDetailRows(details: unknown): BulkCreationDetailRow[] {
  const { rows } = normalizeBulkCreationDetails(details);
  const parkingAudits = readParkingAudits(details);
  const categorySkipAudits = readCategorySkipAudits(details);

  const auditedCategoryNames = new Set<string>([
    ...parkingAudits.map((audit) => audit.feeCategoryName).filter(Boolean),
    ...categorySkipAudits.map((audit) => audit.feeCategoryName).filter(Boolean),
  ]);

  if (auditedCategoryNames.size === 0) {
    return rows;
  }

  const keptRows = rows.filter(
    (row) => row.action !== 'skipped' || !auditedCategoryNames.has(row.feeCategory),
  );
  const expandedSkips = [
    ...expandSkipLogsFromAudits(parkingAudits),
    ...expandSkipLogsFromAudits(categorySkipAudits),
  ];

  return [...keptRows, ...expandedSkips];
}

export function normalizeBulkCreationDetails(
  details: unknown,
): NormalizedBulkCreationDetails {
  if (!details) {
    return { rows: [], warnings: [], categorySummaries: [] };
  }
  if (Array.isArray(details)) {
    return { rows: details as BulkCreationDetailRow[], warnings: [], categorySummaries: [] };
  }
  if (typeof details === 'object' && details !== null) {
    const obj = details as {
      rows?: BulkCreationDetailRow[];
      warnings?: string[];
      parkingAudits?: unknown[];
      categorySkipAudits?: unknown[];
      categorySummaries?: BulkCreationCategorySummary[];
    };
    const rows = Array.isArray(obj.rows) ? obj.rows : [];
    const warnings = Array.isArray(obj.warnings) ? obj.warnings : [];
    const categorySummaries = Array.isArray(obj.categorySummaries)
      ? obj.categorySummaries
      : [];
    return { rows, warnings, categorySummaries };
  }
  return { rows: [], warnings: [], categorySummaries: [] };
}

const ACTION_LABELS: Record<string, string> = {
  created: 'Tạo mới',
  updated: 'Cập nhật',
  skipped: 'Bỏ qua',
  error: 'Lỗi',
};

export function bulkCreationActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function bulkCreationActionBadgeClass(action: string): string {
  switch (action) {
    case 'created':
      return 'bg-green-100 text-green-800';
    case 'updated':
      return 'bg-blue-100 text-blue-800';
    case 'skipped':
      return 'bg-gray-100 text-gray-700';
    case 'error':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}
